import {FilterControlInterface} from 'appShared/interfaces';
import {AmountControl, propertyOf, SelectControl} from 'appShared/classes';
import {ComponentTypesEnum} from 'appShared/enums';
import {Pagination} from 'appShared/components';
import {
    ClientFeedSubscriptionModel,
    ConstraintsCollection,
    ReportFilterConstraintsEnum,
    UserAccountSubscriptionFilterModel,
    UserAccountSubscriptionInterface
} from '@spa/api';

export const ClientMarketDataAccountSelector = 'ClientMarketDataAccount';
export const ClientMarketDataUserSelector = 'ClientMarketDataUser';
export const ClientMarketDataUserCategorySelector = 'ClientMarketDataUserCategory';
export const ClientMarketDataBundleNameSelector = 'ClientMarketDataBundleName';
export const ClientMarketDataPriceFromSelector = 'ClientMarketDataPriceFrom';
export const ClientMarketDataPriceToSelector = 'ClientMarketDataPriceTo';
export const ClientMarketDataMarkupFromSelector = 'ClientMarketDataMarkupFrom';
export const ClientMarketDataMarkupToSelector = 'ClientMarketDataMarkupTo';

export const ClientMarketDataDataFilterControls =
    (hasClientUserPermissions: boolean, hasPricePermissions: boolean, hasMarkupPermissions: boolean): FilterControlInterface[] => {
        const accountControl = {
            name: ClientMarketDataAccountSelector,
            placeholder: 'Account',
            controls: [
                new SelectControl({
                    name: ClientMarketDataAccountSelector,
                    value: null,
                    placeholder: 'Account',
                    validators: [],
                    componentType: ComponentTypesEnum.Select,
                    options: [{
                        label: 'All',
                        value: null
                    }]
                })
            ],
            sortIndex: 1,
            shown: true,
            isRemovable: false
        };

        let controls: any[] = [accountControl];

        if (hasClientUserPermissions) {
            controls = [
                {
                    name: ClientMarketDataUserSelector,
                    placeholder: 'User',
                    controls: [
                        new SelectControl({
                            name: ClientMarketDataUserSelector,
                            value: null,
                            placeholder: 'User',
                            validators: [],
                            componentType: ComponentTypesEnum.Select,
                            options: [{
                                label: 'All',
                                value: null
                            }]
                        })
                    ],
                    sortIndex: 1,
                    shown: true,
                    isRemovable: false
                },
                {...accountControl},
                {
                    name: ClientMarketDataUserCategorySelector,
                    placeholder: 'User Category',
                    controls: [
                        new SelectControl({
                            name: ClientMarketDataUserCategorySelector,
                            value: null,
                            placeholder: 'User Category',
                            validators: [],
                            componentType: ComponentTypesEnum.Select,
                            options: [{
                                label: 'All',
                                value: null
                            }]
                        })
                    ],
                    sortIndex: 1,
                    shown: true,
                    isRemovable: false
                }
            ];
        }

        controls.push({
            name: ClientMarketDataBundleNameSelector,
            placeholder: 'Feed/Bundle Name',
            controls: [
                new SelectControl({
                    name: ClientMarketDataBundleNameSelector,
                    value: null,
                    placeholder: 'Feed/Bundle Name',
                    validators: [],
                    componentType: ComponentTypesEnum.Select,
                    options: [{
                        label: 'All',
                        value: null
                    }]
                })
            ],
            sortIndex: 1,
            shown: true,
            isRemovable: false
        });

        if (hasPricePermissions) {
            controls.push({
                name: ClientMarketDataPriceFromSelector,
                placeholder: 'Price From',
                controls: [
                    new AmountControl({
                        name: ClientMarketDataPriceFromSelector,
                        value: null,
                        placeholder: 'Price From',
                        validators: [],
                        componentType: ComponentTypesEnum.AmountInput,
                        currency: null
                    })
                ],
                sortIndex: 1,
                shown: true,
                isRemovable: false
            });
            controls.push({
                name: ClientMarketDataPriceToSelector,
                placeholder: 'Price To',
                controls: [
                    new AmountControl({
                        name: ClientMarketDataPriceToSelector,
                        value: null,
                        placeholder: 'Price To',
                        validators: [],
                        componentType: ComponentTypesEnum.AmountInput,
                        currency: null
                    })
                ],
                sortIndex: 1,
                shown: true,
                isRemovable: false
            });
        }

        if (hasMarkupPermissions) {
            controls.push({
                name: ClientMarketDataMarkupFromSelector,
                placeholder: 'Markup From',
                controls: [
                    new AmountControl({
                        name: ClientMarketDataMarkupFromSelector,
                        value: null,
                        placeholder: 'Markup From',
                        validators: [],
                        componentType: ComponentTypesEnum.AmountInput,
                        currency: null
                    })
                ],
                sortIndex: 1,
                shown: true,
                isRemovable: false
            });
            controls.push({
                name: ClientMarketDataMarkupToSelector,
                placeholder: 'Markup To',
                controls: [
                    new AmountControl({
                        name: ClientMarketDataMarkupToSelector,
                        value: null,
                        placeholder: 'Markup To',
                        validators: [],
                        componentType: ComponentTypesEnum.AmountInput,
                        currency: null
                    })
                ],
                sortIndex: 1,
                shown: true,
                isRemovable: false
            });
        }

        return controls;
    };

export const defaultUserAccountSubscriptionFilterModel = (itemsPerPage = Pagination.defaultItemsPerPage) => {
    const filter = new UserAccountSubscriptionFilterModel(
        new ConstraintsCollection(),
        Pagination.defaultPagesChunk - 1,
        UserAccountSubscriptionFilterModel.getCreatedTimeSelector(),
        2000,
        false
    );

    filter.addConstraintItem(
        ReportFilterConstraintsEnum.NotEqual,
        propertyOf<UserAccountSubscriptionInterface>('expired'),
        'true'
    );

    filter.addConstraintItem(
        ReportFilterConstraintsEnum.NotEqual,
        propertyOf<UserAccountSubscriptionInterface>('cancelled'),
        'true'
    );

    return filter;
};

export const getCollectionClone = (collection: ClientFeedSubscriptionModel[]): ClientFeedSubscriptionModel[] => {
    const clonedData = [];

    if (!collection || collection.length === 0) {
        return clonedData;
    }

    collection.forEach(model => {
        clonedData.push(new ClientFeedSubscriptionModel({
            account: model.account,
            accountStatus: model.accountStatus,
            accountReference: model.accountReference,
            tradingPlatformId: model.tradingPlatformId,
            tradingPlatformName: model.tradingPlatformName,
            userId: model.userId,
            userFirstName: model.userFirstName,
            userLastName: model.userLastName,
            userCategory: model.userCategory,
            subscriptions: model.subscriptions,
            editMode: model.editMode,
            userStatus: model.userStatus,
            userRelationType: model.userRelationType,
        }));
    });

    return clonedData;
};
